import { Component, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FieldsetModule } from 'primeng/fieldset';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { EmpleadoDetalle } from 'src/app/domain/model/empleado/empleado';
import { formatAsMoneyLegacy } from 'src/app/common/input-utils';

/**
 * Dumb component: "Información Adicional" (nómina, siempre de solo lectura)
 * de "Modificar Empleado/Consultor" (HU1259115).
 *
 * <p>Legacy: `ModificarEmpleadoViewImpl.ui.xml` — segunda sección del
 * formulario, siempre visible (no colapsada por defecto). Los 5 campos de
 * valor monetario (`tbAuxilioTtteFijo`, `tbPrimaUbicacion`,
 * `tbAuxilioTelefonia`, `tbBonificaciones`, `tbSubsidioTransporte`) se
 * formatean con `Utilidades.formatAsMoney()` — ver {@link formatAsMoneyLegacy}.
 * `dstipoContrato` ya llega traducido ("FIJO"/"INDEFINIDO") desde
 * `EmpleadoWebMapper.mapTipoContrato()`.</p>
 *
 * <p>Sin lógica de negocio propia (frontend-girop §14): solo presentación.</p>
 */
@Component({
  selector: 'app-informacion-adicional-empleado',
  standalone: true,
  imports: [CommonModule, FieldsetModule, InputTextModule, TooltipModule],
  template: `
    <p-fieldset legend="Información Adicional" [toggleable]="true" styleClass="mb-4">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 sm:gap-x-6 gap-y-3">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Salario</label>
          <input pInputText readonly class="w-full" [value]="salarioDisplay()" [pTooltip]="'Salario'" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nivel de Estudio</label>
          <input pInputText readonly class="w-full" [value]="detalle().dsnivelEstudio ?? ''" pTooltip="Ingrese Nivel de Estudio" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cargo</label>
          <input pInputText readonly class="w-full" [value]="detalle().dscargo ?? ''" pTooltip="Ingrese Cargo" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fecha Inicio Contrato (aaaa/mm/dd)</label>
          <input pInputText readonly type="date" class="w-full" [value]="detalle().feinicioContrato ?? ''" pTooltip="Fecha Inicio Contrato" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fecha Fin Contrato (aaaa/mm/dd)</label>
          <input pInputText readonly type="date" class="w-full" [value]="detalle().fefinContrato ?? ''" pTooltip="Fecha Fin Contrato" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo Contrato</label>
          <input pInputText readonly class="w-full" [value]="detalle().dstipoContrato ?? ''" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Codigo Nomina</label>
          <input pInputText readonly class="w-full" [value]="detalle().dscodigoNomina ?? ''" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Centro de Costos</label>
          <input pInputText readonly class="w-full" [value]="detalle().dscentroCostos ?? ''" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Jefe Inmediato</label>
          <input pInputText readonly class="w-full" [value]="detalle().dsjefeInmediato ?? ''" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Factor de Tiempo</label>
          <input pInputText readonly class="w-full" [value]="detalle().dsfactorTiempo ?? ''" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">G12 Auxilio Transporte Fijo</label>
          <input pInputText readonly class="w-full" [value]="formatAsMoneyLegacy(detalle().dsauxilioTransp)" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prima de Ubicación J1Q</label>
          <input pInputText readonly class="w-full" [value]="formatAsMoneyLegacy(detalle().dsprimaUbicacion)" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Auxilio de Telefonia J1P</label>
          <input pInputText readonly class="w-full" [value]="formatAsMoneyLegacy(detalle().dsauxilioTelef)" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bonificaciones</label>
          <input pInputText readonly class="w-full" [value]="formatAsMoneyLegacy(detalle().dsbonificaciones)" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subsidio Transporte Legal</label>
          <input pInputText readonly class="w-full" [value]="formatAsMoneyLegacy(detalle().dssubsidioTransp)" />
        </div>
      </div>
    </p-fieldset>
  `,
})
export class InformacionAdicionalEmpleadoComponent {
  readonly detalle = input.required<EmpleadoDetalle>();
  /** Texto de Salario ya formateado (`Utilidades.formatAsMoney()` + regla de tipo de empleado) — calculado por el facade, ver `ModificarEmpleadoFacade.salarioDisplay()`. */
  readonly salarioDisplay = input.required<string>();

  protected readonly formatAsMoneyLegacy = formatAsMoneyLegacy;
}
